/**
 * Auth Store — Module-level Zustand store.
 *
 * Single export `useAuth` works everywhere:
 *   - React hook:    const { user } = useAuth()
 *   - Selector:      const user = useAuth(s => s.user)
 *   - Outside React: useAuth.getState().user
 *
 * AuthProvider handles lifecycle (session validation, focus re-validation)
 * and writes to this shared store. It must wrap the app before any
 * component reads auth state.
 */

import { useEffect, useRef, type ReactNode } from 'react'
import { create } from 'zustand'
import type { AuthConfig, AuthState, AuthUser, SessionResponse } from './types'
import { AuthClient, isAuthCallbackPath } from './client'
import { startKeepalive } from './keepalive'
import { handleUnauthorized } from './unauthorized'

const LOG_PREFIX = '[auth]'

// ─── "Sign-in service unavailable" retry (agion/Agion#2729) ───
//
// An `unavailable` answer from GET /session (5xx, network error) says nothing
// about the session, so it never signs the user out. The wait before the next
// check starts at the server's Retry-After (default 2 s), clamped to 1..30 s,
// and doubles from the second attempt, capped at 30 s: 2, 4, 8, 16, 30, 30...

const DEFAULT_RETRY_AFTER_SEC = 2
const MIN_RETRY_SEC = 1
const MAX_RETRY_SEC = 30
/** Unavailable answers in a row before `sessionCheckUnavailable` is set. */
const UNAVAILABLE_NOTICE_AFTER = 3
/** Focus check: retries after the first unavailable answer before it stops. */
const FOCUS_UNAVAILABLE_RETRIES = 3
/** Wait before re-checking an unauthenticated answer once (soft retry). */
const UNAUTHENTICATED_RETRY_MS = 1_000

/** Wait in ms after the `attempt`-th unavailable answer in a row (1-based). */
function unavailableRetryDelayMs(attempt: number, retryAfterSec?: number): number {
  const base = Math.min(MAX_RETRY_SEC, Math.max(MIN_RETRY_SEC, retryAfterSec ?? DEFAULT_RETRY_AFTER_SEC))
  return Math.min(MAX_RETRY_SEC, base * 2 ** (attempt - 1)) * 1_000
}

// ─── Module-level auth store (singleton) ───

interface AuthStore extends AuthState {
  /** Set authenticated user + session data */
  setAuth: (user: AuthUser, sessionData: SessionResponse) => void
  /** Set loading state */
  setLoading: (loading: boolean) => void
  /** Set error state (clears user) */
  setError: (error: string) => void
  /** Clear all auth state */
  reset: () => void
  /** Mark that the sign-in service keeps answering "unavailable" */
  setSessionCheckUnavailable: (unavailable: boolean) => void
  /** Logout action — clears state and calls client.logout() */
  logout: () => Promise<void>
  /** Auth client ref — set by AuthProvider */
  client: AuthClient | null
  /** Auth config — set by AuthProvider */
  config: AuthConfig | null
}

export const useAuth = create<AuthStore>()((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  sessionData: null,
  sessionCheckUnavailable: false,
  client: null,
  config: null,

  setAuth: (user, sessionData) =>
    set({ user, isAuthenticated: true, isLoading: false, error: null, sessionData, sessionCheckUnavailable: false }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) =>
    set({ user: null, isAuthenticated: false, isLoading: false, error, sessionData: null }),
  reset: () =>
    set({ user: null, isAuthenticated: false, isLoading: false, error: null, sessionData: null, sessionCheckUnavailable: false }),
  setSessionCheckUnavailable: (sessionCheckUnavailable) => set({ sessionCheckUnavailable }),
  logout: async () => {
    const { client } = get()
    set({ user: null, isAuthenticated: false, isLoading: false, error: null, sessionData: null, sessionCheckUnavailable: false })
    if (client) {
      await client.logout()
    }
  },
}))

// ─── AuthProvider (lifecycle only) ───

interface AuthProviderProps {
  config: AuthConfig
  children: ReactNode
}

export function AuthProvider({ config, children }: AuthProviderProps) {
  const clientRef = useRef<AuthClient>(undefined)
  const lastValidationRef = useRef<number>(0)

  // Create client once and inject into store
  if (!clientRef.current) {
    clientRef.current = new AuthClient(config)
    useAuth.setState({ client: clientRef.current, config })
  }

  const client = clientRef.current

  // Validate session on mount
  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const wait = (ms: number) => new Promise<void>((resolve) => { timer = setTimeout(resolve, ms) })

    const validateSession = async () => {
      // The public callback owns its exchange and session validation. A parallel
      // mount probe can reset the session after that exchange has succeeded.
      if (config.hasAuthCallbackRoute && isAuthCallbackPath(window.location.pathname)) return

      // Mock auth mode — no network calls
      if (config.mockAuth) {
        const mockUser: AuthUser = {
          id: config.mockUser?.id ?? 'dev-admin-123',
          email: config.mockUser?.email ?? 'admin@agion.local',
          name: config.mockUser?.name ?? 'Dev Admin',
          role: config.mockUser?.role ?? 'admin',
          organizationId: config.mockUser?.organizationId ?? 'mock-org-123',
          organizationName: config.mockUser?.organizationName ?? 'Mock Organization',
          ...config.mockUser,
        }
        const mockSession: SessionResponse = {
          authenticated: true,
          userId: mockUser.id,
          email: mockUser.email,
          role: mockUser.role,
          organizationId: mockUser.organizationId,
          organizationName: mockUser.organizationName,
        }
        useAuth.getState().setAuth(mockUser, mockSession)
        console.warn(LOG_PREFIX, 'Mock auth enabled — using mock user data')
        return
      }

      useAuth.getState().setLoading(true)

      // An unauthenticated answer is re-checked once after 1 s before the
      // reset. An unavailable answer keeps the loading state and retries with
      // backoff for as long as the page is open: it must never land the user
      // on the sign-in page or /access-denied.
      let unauthenticatedRetried = false
      let unavailableInARow = 0
      for (;;) {
        const result = await client.checkSession()
        if (cancelled) return

        if (result.status === 'ok') {
          useAuth.getState().setAuth(result.user, result.sessionData)
          lastValidationRef.current = Date.now()
          return
        }

        if (result.status === 'unauthenticated') {
          if (!unauthenticatedRetried) {
            unauthenticatedRetried = true
            console.debug(LOG_PREFIX, 'Mount probe found no session, retrying once before reset')
            await wait(UNAUTHENTICATED_RETRY_MS)
            if (cancelled) return
            continue
          }
          console.debug(LOG_PREFIX, 'Mount session check found no session after retry')
          useAuth.getState().reset()
          return
        }

        unavailableInARow += 1
        if (unavailableInARow >= UNAVAILABLE_NOTICE_AFTER) {
          useAuth.getState().setSessionCheckUnavailable(true)
        }
        const delayMs = unavailableRetryDelayMs(unavailableInARow, result.retryAfterSec)
        console.debug(LOG_PREFIX, 'Sign-in service unavailable on mount, retrying', { attempt: unavailableInARow, delayMs })
        await wait(delayMs)
        if (cancelled) return
      }
    }

    validateSession()
    return () => {
      cancelled = true
      if (timer !== undefined) clearTimeout(timer)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // On-focus re-validation with throttle.
  //
  // Soft revalidation: an unauthenticated answer is re-checked once after
  // 1 s, and only the second one calls reset(). An unavailable answer (5xx,
  // network error) keeps the current auth state: the check retries up to
  // FOCUS_UNAVAILABLE_RETRIES times with backoff, then stops; the next focus
  // after the throttle window tries again.
  useEffect(() => {
    if (config.revalidateOnFocus === false || config.mockAuth) return

    const throttleMs = config.revalidateThrottleMs ?? 30_000
    let cancelled = false
    let inFlight = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const wait = (ms: number) => new Promise<void>((resolve) => { timer = setTimeout(resolve, ms) })

    const revalidate = async () => {
      let unauthenticatedRetried = false
      let unavailableInARow = 0
      for (;;) {
        const result = await client.checkSession()
        lastValidationRef.current = Date.now()
        if (cancelled) return
        // Signed out elsewhere while this check was waiting: nothing to keep.
        if (!useAuth.getState().isAuthenticated) return

        if (result.status === 'ok') {
          useAuth.getState().setAuth(result.user, result.sessionData)
          return
        }

        if (result.status === 'unauthenticated') {
          if (!unauthenticatedRetried) {
            unauthenticatedRetried = true
            console.debug(LOG_PREFIX, 'Focus probe found no session, retrying once before reset')
            await wait(UNAUTHENTICATED_RETRY_MS)
            if (cancelled) return
            continue
          }
          console.warn(LOG_PREFIX, 'Session expired during background — clearing auth state')
          useAuth.getState().reset()
          return
        }

        unavailableInARow += 1
        if (unavailableInARow >= UNAVAILABLE_NOTICE_AFTER) {
          useAuth.getState().setSessionCheckUnavailable(true)
        }
        if (unavailableInARow > FOCUS_UNAVAILABLE_RETRIES) {
          console.warn(LOG_PREFIX, 'Sign-in service unavailable on focus — keeping the session, will check on a later focus')
          return
        }
        await wait(unavailableRetryDelayMs(unavailableInARow, result.retryAfterSec))
        if (cancelled) return
      }
    }

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return
      if (inFlight) return
      if (Date.now() - lastValidationRef.current < throttleMs) return

      const { isAuthenticated } = useAuth.getState()
      if (!isAuthenticated) return

      console.debug(LOG_PREFIX, 'Re-validating session on focus')
      inFlight = true
      try {
        await revalidate()
      } finally {
        inFlight = false
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      cancelled = true
      if (timer !== undefined) clearTimeout(timer)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Proactive keepalive. Drives rolling refresh + Redis SessionTTL reset
  // so an idle UI page does not let the session lapse. Multi-tab leader
  // election via Web Locks ensures only one tab in the browser fires.
  useEffect(() => {
    if (config.mockAuth) return
    if (config.enableKeepalive === false) return

    const isAuthed = useAuth.getState().isAuthenticated
    // First mount: keepalive needs an authenticated session to be useful.
    // Subscribe to the store so we start the loop once auth completes,
    // and stop it on logout.
    let stopKeepalive: (() => void) | null = null

    const ensureRunning = (authed: boolean) => {
      if (authed && !stopKeepalive) {
        stopKeepalive = startKeepalive({
          client,
          intervalMs: config.keepaliveIntervalMs,
          touchUrl: config.keepaliveUrl,
          onUnauthorized: handleUnauthorized,
        })
      } else if (!authed && stopKeepalive) {
        stopKeepalive()
        stopKeepalive = null
      }
    }

    ensureRunning(isAuthed)
    const unsubscribe = useAuth.subscribe((state, prev) => {
      if (state.isAuthenticated !== prev.isAuthenticated) {
        ensureRunning(state.isAuthenticated)
      }
    })

    return () => {
      unsubscribe()
      if (stopKeepalive) {
        stopKeepalive()
        stopKeepalive = null
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return <>{children}</>
}

// ─── Legacy aliases ───

/** @deprecated Use useAuth directly — it's a Zustand store */
export function useAuthStore() {
  return useAuth
}

/** Access auth config (apiUrl, authUrl, loginUrl, appName) */
export function useAuthConfig(): AuthConfig {
  const config = useAuth(s => s.config)
  if (!config) throw new Error('AuthProvider not mounted yet')
  return config
}

// Re-export the UseAuthReturn type (same shape as store state)
export type UseAuthReturn = AuthStore
